import config from 'constants/config'
import { createSelector } from 'reselect'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { NAVBAR_SPECIFIC_ORDER_REGIONS_LIST } from 'constants/config/region'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getNavItemsFlat } from 'constants/config/navItemsByRegion'
import { getAgentHubAffiliationRules } from 'agentHub/selectors/agentHubSelectors'
import { LabFeatureFlags } from 'constants/lab'
import { isLabSiteTakeoverPayload } from 'lib/lab/dynamicPayload'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyEnabledDecision, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { isLEStaff } from './accountSelectors'

const HOMES_AND_VILLAS_NAV_ID = 'nav-link-homes-and-villas'
export const MALDIVES_BUNDLES_NAV_ID = 'nav-link-maldives-packages'

const reorderHomesAndMaldives = (
  navItems: Array<App.NavItem>,
  shouldPlaceHomesBeforeMaldives: boolean,
): Array<App.NavItem> => {
  const homesIndex = navItems.findIndex(item => item.id === HOMES_AND_VILLAS_NAV_ID)
  const maldivesIndex = navItems.findIndex(item => item.id === MALDIVES_BUNDLES_NAV_ID)

  if (homesIndex === -1 || maldivesIndex === -1) {
    return navItems
  }

  const nextItems = [...navItems]
  const [homesItem] = nextItems.splice(homesIndex, 1)
  const maldivesIndexAfterRemoval = nextItems.findIndex(item => item.id === MALDIVES_BUNDLES_NAV_ID)
  if (maldivesIndexAfterRemoval === -1) {
    return navItems
  }
  const insertIndex = shouldPlaceHomesBeforeMaldives ? maldivesIndexAfterRemoval : maldivesIndexAfterRemoval + 1
  nextItems.splice(insertIndex, 0, homesItem)
  return nextItems
}

const extractHomesFromDropdown = (
  navItems: Array<App.NavItem>,
): { items: Array<App.NavItem>; homesItem?: App.NavItem } => {
  let homesItem: App.NavItem | undefined
  const items = navItems.reduce<Array<App.NavItem>>((nextItems, navItem) => {
    if (!navItem.hasDropdown || !navItem.items) {
      nextItems.push(navItem)
      return nextItems
    }

    const nextDropdownItems = navItem.items.filter(item => item.id !== HOMES_AND_VILLAS_NAV_ID)
    if (nextDropdownItems.length !== navItem.items.length) {
      homesItem = navItem.items.find(item => item.id === HOMES_AND_VILLAS_NAV_ID) ?? homesItem
    }

    if (nextDropdownItems.length > 0) {
      nextItems.push({
        ...navItem,
        items: nextDropdownItems,
      })
    }

    return nextItems
  }, [])

  return { items, homesItem }
}

const insertHomesBeforeMaldives = (
  navItems: Array<App.NavItem>,
  homesItem?: App.NavItem,
): Array<App.NavItem> => {
  if (!homesItem) {
    return navItems
  }

  if (navItems.some(item => item.id === HOMES_AND_VILLAS_NAV_ID)) {
    return reorderHomesAndMaldives(navItems, true)
  }

  const maldivesIndex = navItems.findIndex(item => item.id === MALDIVES_BUNDLES_NAV_ID)
  if (maldivesIndex === -1) {
    return navItems
  }

  const nextItems = [...navItems]
  nextItems.splice(maldivesIndex, 0, homesItem)
  return nextItems
}

export const getNavItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.system.storeMode,
  (state: App.State) => state.optimizely.optimizelyFeatureFlags,
  (state: App.State) => getAgentHubAffiliationRules(state),
  (state: App.State) => state.system.features.labFlags,
  (state: App.State) => getOptimizelyExperimentVariation(state, OptimizelyExperiments.navHomesVillasBeforeMaldivesBundles),
  (state: App.State) => state.geo.geoStateName,
  (state: App.State) => isLEStaff(state),
  (account, isLuxPlusEnabled, isLoyaltyEnabled, currentRegionCode, isStoreMode, featureFlags, affiliationRules, labFlags, navOrderExperimentVariation, geoStateName, isStaff) => {
    const enabledForAgenthubAgent = (nav: App.NavItem) => {
      if (nav.id === 'nav-link-car-hire') {
        return affiliationRules.canBookCarHire
      }

      if (nav.id === 'nav-link-cruises') {
        return affiliationRules.canBookCruise
      }

      return true
    }

    const navEnabled = (nav: App.NavItem) => {
      if (nav.onlyOnRegions && !nav.onlyOnRegions.includes(currentRegionCode)) {
        return false
      }

      if (nav.notOnRegions?.includes(currentRegionCode)) {
        return false
      }

      if (geoStateName && nav.notOnGeoStates?.includes(geoStateName)) {
        return false
      }

      if (nav.onlyOnGeoStates && (!geoStateName || !nav.onlyOnGeoStates.includes(geoStateName))) {
        return false
      }

      if (nav.featureToggle && !config[nav.featureToggle]) {
        return false
      }

      if (nav.optimizelyFeatureToggle && !featureFlags[nav.optimizelyFeatureToggle]) {
        return false
      }

      if (nav.accountToggle && (!account[nav.accountToggle])) {
        return false
      }

      if (nav.staffOnly && !isStaff) {
        return false
      }

      if (!!nav.luxPlusToggle && !isLuxPlusEnabled) {
        return false
      }

      if (!!nav.loyaltyToggle && !isLoyaltyEnabled) {
        return false
      }

      if (nav.hideInStoreMode && isStoreMode) {
        return false
      }

      if (nav.labCampaignFeatureToggle) {
        const labFlag = labFlags[LabFeatureFlags.enableSiteTakeover]
        if (labFlag?.enabled) {
          const payload = labFlag.value
          if (isLabSiteTakeoverPayload(payload)) {
            return payload.id === nav.labCampaignFeatureToggle
          }
        }
        return false
      }

      if (nav.id && !enabledForAgenthubAgent(nav)) {
        return false
      }

      return true
    }

    const navRegionConfig = NAVBAR_SPECIFIC_ORDER_REGIONS_LIST.has(currentRegionCode) ? currentRegionCode : 'DEFAULT'
    const navItems = Array.isArray(config.header.nav.items) ? config.header.nav.items : config.header.nav.items[navRegionConfig]
    const shouldPlaceHomesBeforeMaldives = getOptimizelyEnabledDecision(navOrderExperimentVariation)
    const filteredItems = navItems.reduce<Array<App.NavItem>>((navList, nav) => {
      if (nav.newLabelOnlyOnRegions) nav.newLabel = nav.newLabelOnlyOnRegions.includes(currentRegionCode)

      if (nav.hasDropdown) {
        // need to whittle down the dropdown items of this subnav
        // note: subnavs can only go 1 level deep at most

        const subNav = {
          ...nav,
          items: nav.items?.filter(navEnabled),
        }
        if (subNav.items && subNav.items.length > 0) {
          navList.push(subNav)
        }
      } else if (navEnabled(nav)) {
        // normal nav, see if we can push it in
        navList.push(nav)
      }
      return navList
    }, [])
    if (!shouldPlaceHomesBeforeMaldives) {
      return filteredItems
    }

    const { items: itemsWithoutHomesInDropdown, homesItem } = extractHomesFromDropdown(filteredItems)
    return insertHomesBeforeMaldives(itemsWithoutHomesInDropdown, homesItem)
  },
)

/**
 * Retrieves the nav items flattened
 * This is used in the LuxKit navbar
 */
export const getFlatNavItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.system.storeMode,
  (state: App.State) => state.optimizely.optimizelyFeatureFlags,
  (state: App.State) => state.system.features.labFlags,
  (state: App.State) => getOptimizelyExperimentVariation(state, OptimizelyExperiments.navHomesVillasBeforeMaldivesBundles),
  (state: App.State) => state.geo.geoStateName,
  (state: App.State) => isLEStaff(state),
  (account, isLuxPlusEnabled, isLoyaltyEnabled, currentRegionCode, isStoreMode, featureFlags, labFlags, navOrderExperimentVariation, geoStateName, isStaff): Array<App.NavItem> => {
    const shouldPlaceHomesBeforeMaldives = getOptimizelyEnabledDecision(navOrderExperimentVariation)
    const updateHomesNavItemForAU = (navItem: App.NavItem): App.NavItem => {
      if (currentRegionCode !== 'AU' || navItem.id !== 'nav-link-homes-and-villas') {
        return navItem
      }
      return {
        ...navItem,
        to: '/rentals',
      }
    }
    const filteredItems = getNavItemsFlat().filter(nav => {
      if (nav.onlyOnRegions && !nav.onlyOnRegions.includes(currentRegionCode)) {
        return false
      }

      if (nav.notOnRegions?.includes(currentRegionCode)) {
        return false
      }

      if (geoStateName && nav.notOnGeoStates?.includes(geoStateName)) {
        return false
      }

      if (nav.onlyOnGeoStates && (!geoStateName || !nav.onlyOnGeoStates.includes(geoStateName))) {
        return false
      }

      if (nav.featureToggle && !config[nav.featureToggle]) {
        return false
      }

      if (nav.optimizelyFeatureToggle && !featureFlags[nav.optimizelyFeatureToggle]) {
        return false
      }

      if (nav.accountToggle && (!account[nav.accountToggle])) {
        return false
      }

      if (nav.staffOnly && !isStaff) {
        return false
      }

      if (!!nav.luxPlusToggle && !isLuxPlusEnabled) {
        return false
      }

      if (!!nav.loyaltyToggle && !isLoyaltyEnabled) {
        return false
      }

      if (nav.hideInStoreMode && isStoreMode) {
        return false
      }

      if (nav.labCampaignFeatureToggle) {
        const labFlag = labFlags[LabFeatureFlags.enableSiteTakeover]
        if (labFlag?.enabled) {
          const payload = labFlag.value
          if (isLabSiteTakeoverPayload(payload)) {
            return payload.id === nav.labCampaignFeatureToggle
          }
        }
        return false
      }

      return true
    },
    )
      .map(updateHomesNavItemForAU)
    if (!shouldPlaceHomesBeforeMaldives) {
      return filteredItems
    }

    return reorderHomesAndMaldives(filteredItems, true)
  },
)
